import * as React from "react";
import { TooltipHost, Stack, IStackTokens, PanelType, ITag, TagPicker, Label, IBasePickerSuggestionsProps, Spinner, SpinnerSize, FontIcon } from "@fluentui/react";
import { LanguageContext } from "../../../../../../../utilities/LocalizationModule";
import { INameValuePairIndex } from "../../../../../interfaces/INameValuePairIndex";
import { DatasourceService } from "../../../../../services/DatasourceService";
import { DatasourceHelper } from "../../../../helpers/dataSourceHelper";
import { DataSourcePanel } from "../../../../inputs/datasourcePanel";
import { IBaseInputProps } from "../../../../../interfaces/advancedSearch/IBaseInputProps";
import advancedSearchStateManager from "../../../../../../../advancedSearch/advancedSearchStateManager";
import { IDatasourceQueryDto } from "../../../../../interfaces/IDatasourceQueryDto";
import { logger } from "../../../../../../../utilities/logger";
import { filterSuggestedTagList, sortTerms } from "../../../../helpers/tagHelper";

const DatasourceTagPickerInput: React.FunctionComponent<IBaseInputProps> = props => {
    const languageStrings = React.useContext(LanguageContext);
    const [isOpen, setIsOpen] = React.useState(false);
    const [searchInputs, setSearchInputs] = React.useState<INameValuePairIndex[]>([]);
    const [boundedField, setBoundedField] = React.useState<string>("");
    const dataSourceService = new DatasourceService();
    const dataSourceHelper = new DatasourceHelper(dataSourceService);
    // useMemo, NOT a one-shot useState initializer: the client-type context can settle after
    // mount (setDatasourceFields is re-frozen then), and a captured verdict here would disagree
    // with the control-type path - the empty-dropdown bug. getInputDataSource returns the same
    // object while the verdict is unchanged, so the [dataSourceItem] effect below is stable.
    const dataSourceItem = React.useMemo(
        () => DatasourceHelper.getInputDataSource(advancedSearchStateManager.dataSourceItems, props.displayName, props.selectedSites, props.selectedLibraries, props.clientTypes),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.clientTypes]
    );
    const [selectedTags, setSelectedTags] = React.useState<ITag[]>([]); //selected tags
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    const cachedSuggestions = React.useRef<ITag[] | undefined>(undefined);
    // Bumped whenever the resolved datasource changes; async work captures it at start and
    // discards its writes if outdated - a stale run resuming after a datasource change must
    // not stomp the new datasource's state or re-poison the suggestion cache.
    const datasourceEpochRef = React.useRef<number>(0);

    React.useEffect(() => {
        // The datasource changed (a client-type verdict settled): suggestions cached from
        // the previous datasource's "*" query belong to it, not to the new one - serving
        // them would offer an EXCLUDED datasource's values. Same clear the taskpane picker
        // performs; the epoch bump additionally invalidates a query still in flight.
        const epoch: number = ++datasourceEpochRef.current;
        cachedSuggestions.current = undefined;
        const setupAsyncWrapper = async () => {
            setIsLoading(true);
            try {
                const result = await dataSourceHelper.getSearchStructure(dataSourceItem);
                if (epoch !== datasourceEpochRef.current) {
                    return;
                }
                if (Array.isArray(result)) setSearchInputs(result.map((item, index) => ({ name: item, value: null, index: index })));
                const boundedFieldName = DatasourceHelper.getBoundedField(dataSourceItem, props.displayName);
                setBoundedField(boundedFieldName ?? "");
            } catch (error) {
                logger(error, "datasourceTagPickerAdvancedSearch.tsx (useEffect)");
            }
            if (epoch === datasourceEpochRef.current) {
                setIsLoading(false);
            }
        };

        setupAsyncWrapper();
        // Keyed on the resolved datasource so a client-type verdict that settles after mount
        // reloads the structure instead of being silently ignored.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSourceItem]);

    React.useEffect(() => {
        const newSelectedTagValue = props.searchInputData?.value
            ? !Array.isArray(props.searchInputData?.value) ? props.searchInputData?.value : props.searchInputData?.value[0]
            : null;
        const newSelectedTags: ITag[] = newSelectedTagValue ? newSelectedTagValue.split(',').map(t => {
            return ({
                name: t,
                key: t,
            })
        }) : [];
        updateSearch(newSelectedTags.length === 1 ? newSelectedTags[0].key as string : "");
        setSelectedTags(newSelectedTags);
    }, [props.searchInputData.value]);

    const getTextFromItem = (item: ITag) => item.name;

    const filterSuggestedTags = async (filterText: string, tagList: ITag[]): Promise<ITag[]> => {
        const getAllSuggestions: boolean = filterText === "*";
        // Captured at query start: a datasource change while the Search is in flight makes
        // this query's rows the OLD datasource's - never cache them.
        const epochAtQueryStart: number = datasourceEpochRef.current;
        let suggestedTags: ITag[] = [];
        if (filterText?.trim()) {
            try {
                let result: any[];
                if (getAllSuggestions && Array.isArray(cachedSuggestions.current) && cachedSuggestions.current.length > 0) {
                    result = cachedSuggestions.current;
                }
                else {
                    const request: IDatasourceQueryDto = {
                        id: dataSourceItem.id,
                        name: dataSourceItem.name,
                        tenantId: dataSourceItem.tenantId,
                        typeId: dataSourceItem.type.id,
                        typeName: dataSourceItem.type.name,
                        queryParams: !getAllSuggestions
                            ? [{ name: boundedField, value: filterText.trim() }]
                            : null,
                        returnOnlyQueriedProperties: true,
                        allowCache: dataSourceItem.allowCache
                    };
                    result = await dataSourceService.Search(request);
                }

                if (epochAtQueryStart !== datasourceEpochRef.current) {
                    // Datasource changed while the query was in flight: these are the OLD
                    // datasource's rows - neither display nor cache them.
                    return [];
                }

                if (result.length > 0) {
                    const boundFieldProperty: string = Object.keys(result[0]).find(key => key.toLowerCase().trim() === boundedField.toLowerCase().trim());
                    if (boundFieldProperty) {
                        if (getAllSuggestions
                            && epochAtQueryStart === datasourceEpochRef.current
                            && (!Array.isArray(cachedSuggestions.current) || cachedSuggestions.current.length === 0)) {
                            cachedSuggestions.current = result;
                        }

                        suggestedTags = result
                            .map<ITag>(resultRow => {
                                const propertyValue = resultRow[boundFieldProperty]?.trim();
                                return {
                                    name: propertyValue,
                                    key: propertyValue
                                }
                            });
                    }
                }

                // Allow custom entries for searching purposes: this picker is a search FILTER, so
                // a value the datasource does not carry still has to be searchable.
                //
                // Never on the browse path. "*" is the sentinel onEmptyResolveSuggestions
                // synthesises, not something the user typed, so offering it puts a literal
                // asterisk in the filter - which matches nothing, since "*" is not a wildcard
                // anywhere in Advanced Search. Only surfaces once a datasource answers the browse
                // with no rows, which is every type except SharePoint.
                if (suggestedTags.length === 0 && !getAllSuggestions) {
                    suggestedTags.push({ name: filterText, key: filterText });
                }

                if (suggestedTags.length > 1) {
                    suggestedTags = filterSuggestedTagList(suggestedTags, filterText, tagList, getAllSuggestions)
                        .slice(0, 50);
                }
            } catch (error) {
                logger(error, "datasourceTagPickerAdvancedSearch.tsx (filterSuggestedTags)");
            }
        }
        return sortTerms(filterText, suggestedTags);
    };

    const onEmptyResolveSuggestions = async (selectedItems?: ITag[]): Promise<ITag[]> => {
        return await filterSuggestedTags("*", selectedItems);
    }

    // const pickerSuggestionsProps: IBasePickerSuggestionsProps = {
    //     suggestionsHeaderText: languageStrings.Suggestions,
    //     noResultsFoundText: languageStrings.NoResultsFound,
    // };

    const closePanel = () => {
        advancedSearchStateManager.isDataSourcePanelActive = false;
        setIsOpen(false);
    };

    const updateInputValue = (newValue: string) => {
        props.handleValueChange(props.searchInputData?.index, [newValue]);
        updateSearch(newValue);
    };

    const updateSearchInputs = (index: number, value: string) => {
        DatasourceHelper.updateSearchInputs(index, value, searchInputs, setSearchInputs);
    };

    const openPanel = async () => {
        advancedSearchStateManager.isDataSourcePanelActive = true;
        // updateSearch(props.searchInputData ? props.searchInputData?.value : "");
        setIsOpen(true);
    };

    const updateSearch = (newValue: string) => {
        const index = searchInputs.findIndex(item => item.name?.trim().toLowerCase() === boundedField?.trim().toLowerCase());
        updateSearchInputs(index, newValue);
    };

    const onChangeHandle = (tags: ITag[]): void => {
        // updateSearch(tags.map(tag => tag.key).join(', ') as string);
        updateSearch(tags?.length === 1 ? tags[0].key as string : "");
        props.handleValueChange(props.searchInputData?.index, tags.map(tag => tag.key).join(', ') as string);
    };

    const input = (
        <>
            <Label>{props.displayName}</Label>
            <TagPicker
                className="xb-sp-input"
                removeButtonAriaLabel="Remove"
                selectedItems={selectedTags}
                onResolveSuggestions={filterSuggestedTags}
                onEmptyResolveSuggestions={onEmptyResolveSuggestions}
                resolveDelay={300}
                getTextFromItem={getTextFromItem}
                // pickerSuggestionsProps={pickerSuggestionsProps}
                onChange={onChangeHandle}
            />
        </>
    );

    const stackTokens: IStackTokens = {
        childrenGap: 5
    };

    return (
        <>
            <Stack horizontal verticalAlign="start" tokens={stackTokens}>
                <Stack.Item grow={1}>{input}</Stack.Item>
                <Stack.Item>
                    {isLoading
                        ? <Spinner size={SpinnerSize.small} style={{ paddingTop: 36, marginLeft: -2 }} />
                        : <div className="xb-search-icon-container">
                            <TooltipHost content={`${languageStrings.Search} ${dataSourceItem?.name} ${languageStrings.Datasource}`}>
                                <FontIcon iconName="Search" onClick={openPanel} />
                            </TooltipHost>
                        </div>}
                </Stack.Item>
            </Stack>
            <DataSourcePanel
                type={PanelType.customNear}
                displayName={dataSourceItem?.name}
                closePanel={closePanel}
                isOpen={isOpen}
                datasourceItem={dataSourceItem}
                datasourceService={dataSourceService}
                searchInputs={searchInputs}
                updateSearchInputs={updateSearchInputs}
                updateInputValue={updateInputValue}
                boundedField={boundedField}
            />
        </>
    );
};

export default DatasourceTagPickerInput;